import React, { CSSProperties, PropsWithChildren, useEffect, useState } from "react";
import classnames from "classnames";
import { ExtendedDataNode, foldersStore } from "../Stores/FoldersStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AutoSizer as _AutoSizer, AutoSizerProps, List as _List, ListProps } from "react-virtualized";
const AutoSizer = _AutoSizer as unknown as React.FC<AutoSizerProps>;
const List = _List as unknown as React.FC<ListProps>;

interface DocumentsTreeProps {
  defaultExpandedKeys: Array<number>; //currently expanded tree
  defaultSelectedKeys: Array<number | string>;
  onSelect: (selectedKeys: number, linkId: number) => void;
  onExpand: (expandedKeys: number, expanded: boolean) => void;
  selectedKeys: Array<number>; // selected folder
  loadData: (id: number) => Promise<void>;
  treeData: ExtendedDataNode[];
  droppable?: boolean; // allow for drop from outside
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  switcherIcon?: any;
  onDrop?: (event: React.DragEvent, nodeId: number) => void;
  style?: CSSProperties;
  isDragging?: boolean; // is dragging active
}

function traverseTreeData(target, depth: number = 0) {
  return target
    .map((data) => {
      const isExpanded = foldersStore.expandedKeys.includes(data.id);
      const node = { depth, node: data, expanded: isExpanded };
      return data.children ? (isExpanded ? [node, ...traverseTreeData(data.children, depth + 1)] : node) : node;
    })
    .flat();
}
function getPath(items: ExtendedDataNode[], id, path = []) {
  for (const item of items) {
    if (item.id === id) return [item];
    if (item.children instanceof Array) {
      const p = getPath(item.children, id, path);
      if (p !== undefined) return [...p, item];
    }
  }
}

export const DocumentsTree = (props: DocumentsTreeProps) => {
  useEffect(() => {
    if (props.selectedKeys?.[0] && props.treeData !== undefined) {
      getPath(props.treeData, props.selectedKeys[0])
        ?.map((e) => e.id)
        .reverse()
        .forEach((id) => props.onExpand(id, true));
    }
  }, [props.selectedKeys, props.treeData]);

  const flattenTreeData = traverseTreeData(props.treeData);
  const list: TreeNodeProps[] = flattenTreeData.map(({ depth, node: item, expanded }) => {
    return {
      expanded: expanded,
      depth: depth,
      onSelect: props.onSelect,
      onExpand: props.onExpand,
      key: item.id,
      id: item.id,
      linkId: item.linkId,
      title: item.title,
      directFiles: item.directFiles,
      defaultExpandedKeys: props.defaultExpandedKeys,
      selectedKeys: props.selectedKeys,
      children: item.children,
      hasChildren: item.hasChildren,
      loadData: props.loadData,
      onDrop: props.onDrop,
      switcherIcon: props.switcherIcon,
      isDragging: props.isDragging,
      // not droppable for external-  (!item.linkId) and internal- (item.id > 0) links
      droppable: !item.linkId && item.id > 0 && props.droppable,
    };
  });
  return (
    <div className="document-tree-list" style={props.style}>
      <AutoSizer>
        {({ width, height }) => (
          <List
            width={width}
            height={height}
            rowCount={list.length}
            rowHeight={25}
            rowRenderer={({ index, key, style }) => {
              return (
                <div style={style} key={key}>
                  <TreeNode {...list[index]} />
                </div>
              );
            }}
          />
        )}
      </AutoSizer>
    </div>
  );
};

interface TreeNodeProps extends PropsWithChildren {
  id: number;
  title: string;
  expanded: boolean;
  linkId: number;
  directFiles: number;
  defaultExpandedKeys: Array<number>;
  selectedKeys: Array<number>;
  onExpand: (expandedKeys: number, expanded: boolean) => void;
  onSelect: (selectedKeys: number, linkId?: number) => void;
  loadData: (id: number) => Promise<void>;
  depth: number;
  hasChildren: boolean;
  onDrop: (event: React.DragEvent, id: number) => void;
  switcherIcon: (expanded: boolean, loading?: boolean) => React.ReactNode;
  isDragging?: boolean;
  droppable?: boolean;
}

const TreeNode = (props: TreeNodeProps) => {
  const [loading, setLoading] = useState(false);
  const handleExpand = async () => {
    try {
      setLoading(true);
      const { id, expanded, loadData, onExpand } = props;
      onExpand(id, !expanded);
      await loadData(id);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (props.droppable) {
      e.currentTarget.classList.add("droppable-row");
    }
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (props.droppable) {
      e.currentTarget.classList.remove("droppable-row");
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const { onDrop, id, droppable } = props;
    if (droppable) {
      onDrop(e, id);
      e.currentTarget.classList.remove("droppable-row");
    }
  };

  return (
    <div
      onDrop={onDrop}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={(e) => e.preventDefault()}
      className={classnames("documents-tree-treenode", {
        "empty-folder": !props.directFiles,
      })}
      style={{ marginLeft: `${props.depth * 10}px`, whiteSpace: "nowrap" }}
    >
      <span
        className={classnames(
          "documents-tree-switcher",
          props.expanded ? "documents-tree-switcher_open" : "documents-tree-switcher_close",
          props.hasChildren ? "documents-tree-switcher-invisible" : "documents-tree-switcher-visible"
        )}
        onClick={handleExpand}
      >
        {loading ? (
          <FontAwesomeIcon icon={"spinner"} spin fixedWidth={true} className="mr-5" />
        ) : (
          props.switcherIcon(props.expanded)
        )}
      </span>

      <span
        onClick={() => props.onSelect(props.id)}
        title={props.title}
        className={classnames("documents-tree-node-content-wrapper", {
          "documents-tree-node-selected": props.id === props.selectedKeys[0],
          "documents-tree-isdragging": props.isDragging,
        })}
      >
        <span className="documents-tree-title">{props.title}</span>
      </span>
    </div>
  );
};
